import capitalize from 'lodash/capitalize';
import * as Yup from 'yup';

export const MESSAGES = {
  loading: {
    title: 'Gathering your messages'
  },
  empty: {
    title: 'No messages, try later! Thanks for staying up to date.'
  },
  error: {
    title: "Sorry, your messages didn't load. Refresh or come back in a bit!"
  }
};

export const UPDATES = {
  loading: {
    title: 'Gathering your updates',
    subTitle: 'Thanks for being patient!'
  },
  empty: {
    title: "You don't seem to have any updates. Any new updates will show up here!"
  },
  error: {
    title: "Sorry, we couldn't find your updates. Try again or come back in a bit!",
    subtitle: 'Report the Issue'
  }
};

export const CarouselLoadingPrint = {
  title: 'Hang in there',
  subtitle: 'While we gather your data.'
};

export const createCarouselErrorPrint = (errors?: string[]) => {
  const title = "We couldn't find your info";

  const subtitle = errors?.length
    ? "We're sorry, some of your to-dos are unavailable at the moment."
    : "We're sorry, your to-dos are unavailable at the moment.";

  const detailsTitle = "We couldn't find these to-dos";

  const detailsSubtitle = capitalize(errors?.join(', ') ?? '');

  return {
    title,
    subtitle,
    detailsTitle,
    detailsSubtitle
  };
};

export const initialValues = {
  date: null,
  location: '',
  address: '',
  city: '',
  zip: '',
  state: '',
  providerName: '',
  phone: '',
  providerGroupName: '',
  outCome: '',
  isTermsAccepted: false
};

export const defaultHealthAdvisorySchema = Yup.object().shape({
  date: Yup.date()
    .typeError('Invalid Date')
    .required('Invalid Date'),
  location: Yup.string().required('Location is required'),
  providerGroupName: Yup.string().required('Provider pool is required'),
  providerName: Yup.string().required('Provider Name is required'),
  isTermsAccepted: Yup.bool().oneOf([true], 'Field must be checked')
});

export const fluShotSchemas = Yup.object().shape({
  date: Yup.date()
    .typeError('Invalid Date')
    .required('Invalid Date'),
  location: Yup.string().required('Location is required'),
  providerGroupName: Yup.string().required('Provider pool is required'),
  isTermsAccepted: Yup.bool().oneOf([true], 'Field must be checked')
});

export interface QuickLink {
  iconName: string;
  label: string;
  mobilePath: string;
  webPath: string;
  params?: { [params: string]: unknown };
  labelWidth?: number;
}

export interface Quicklinks {
  FIND_A_PROVIDER: QuickLink;
  SCHEDULE_APPOINTMENT: QuickLink;
  CONNECT_CARE_VIDEO_VISIT: QuickLink;
  CHECK_SYMPTOMS: QuickLink;
  TEST_RESULTS: QuickLink;
  CURRENT_MEDICATIONS: QuickLink;
  IMMUNIZATION_RECORDS: QuickLink;
  VISIT_SUMMARIES: QuickLink;
}

/**
  Note: If there's a change in some of the labels you need to change the labelWidth too
  to ensure labels take two rows. Also, labels need to be in sentence case.
*/
export const DASHBOARD_QUICKLINKS: Quicklinks = {
  FIND_A_PROVIDER: {
    iconName: 'QLProviderSearch',
    label: 'Find a Provider',
    mobilePath: 'ProviderSearch',
    webPath: '/u/find-a-doctor',
    labelWidth: 65
  },
  SCHEDULE_APPOINTMENT: {
    iconName: 'QLScheduleAppointment',
    label: 'Schedule Appointment',
    mobilePath: 'BookingSpecialtyOrMyDoctorSelect',
    webPath: '/u/get-care-now/booking/patient-select'
  },
  CONNECT_CARE_VIDEO_VISIT: {
    iconName: 'QLConnectCare',
    label: 'Virtual Visit',
    mobilePath: 'ConnectCareLanding',
    webPath: '/u/get-care-now/connect-care'
  },
  CHECK_SYMPTOMS: {
    iconName: 'QLCheckSymptoms',
    label: 'Check Symptoms',
    mobilePath: 'GyantTriageTerms',
    webPath: '/u/assess-my-symptoms/terms',
    labelWidth: 70
  },
  TEST_RESULTS: {
    iconName: 'QLTestResults',
    label: 'Test Results',
    mobilePath: 'TestResultsHealthRecordsList',
    webPath: '/u/health-record/test-results',
    labelWidth: 60
  },
  CURRENT_MEDICATIONS: {
    iconName: 'QLMedicationList',
    label: 'Medication List',
    mobilePath: 'MedicationsScreen',
    webPath: '/u/health-record/current-medications',
    labelWidth: 74,
    params: {
      medicationType: 'current'
    }
  },
  IMMUNIZATION_RECORDS: {
    iconName: 'QLImmunizations',
    label: 'Immunization Records',
    mobilePath: 'Immunizations',
    webPath: '/u/health-record/immunizations'
  },
  VISIT_SUMMARIES: {
    iconName: 'QLVisitSummary',
    label: 'Visit Summaries',
    mobilePath: 'VisitSummarySearch',
    webPath: '/u/health-record/visit-summaries',
    labelWidth: 74
  }
};

export const RTA_BREAKDOWN = {
  rtaItemOne: {
    titleText: 'View itemized charges',
    subtitleText: 'Your cost for this service:'
  },
  rtaItemTwo: {
    titleText: 'View payments and adjustments',
    subtitleText: 'Expected Insurance Payment',
    subtitleTwoText: 'Expected adjustments/discounts'
  },
  rtaItemThree: {
    titleText: 'View claims',
    subtitleText: 'Plan Eligible Amount:'
  },
  noClaims: 'No Claims Available'
};

export const DASHBOARD_BANNER_INDEX = 'dashboardBannerIndex';

export enum DASHBOARD_CARD_CATEGORIES {
  PREVENTIVE_CARE_ADVISORIES = 'Preventive Care Advisories',
  ACCOUNT = 'Account',
  DOCTOR_PRESCRIBED = 'Doctor Prescribed',
  RESULTS = 'Results',
  BILLING = 'Billing'
}

export const dashboardCardCategory = {
  onboarding: DASHBOARD_CARD_CATEGORIES.ACCOUNT,
  avatarsCardFemale: DASHBOARD_CARD_CATEGORIES.ACCOUNT,
  avatarsCardMale: DASHBOARD_CARD_CATEGORIES.ACCOUNT,
  needActivation: DASHBOARD_CARD_CATEGORIES.ACCOUNT,
  goals: DASHBOARD_CARD_CATEGORIES.DOCTOR_PRESCRIBED,
  pending: DASHBOARD_CARD_CATEGORIES.RESULTS,
  completed: DASHBOARD_CARD_CATEGORIES.RESULTS,
  anticoagulation: DASHBOARD_CARD_CATEGORIES.RESULTS,
  rtas: DASHBOARD_CARD_CATEGORIES.BILLING
};
